import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useSimpleExerciseSearch } from '@/hooks/useSimpleExerciseSearch';
import { useExerciseBookmarks } from '@/hooks/useExerciseBookmarks';
import { useAthletes } from '@/hooks/useAdvancedExerciseSearch';
import { Copy } from '@phosphor-icons/react';
import {
  Loader2, Video, VideoOff, Search, Play, User, UserPlus, Check, X,
  SlidersHorizontal, Bookmark, BookmarkCheck, Dumbbell, Mic, MicOff,
  Microscope, BookOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import BookmarkForAthleteDialog from '@/components/exercises/BookmarkForAthleteDialog';

const calculateCompletion = (exercise, evidenceCount = 0) => {
  let percentage = 0;
  const parts = [];
  if (exercise.has_video) { percentage += 25; parts.push("Video"); }
  if (exercise.demonstrator_name) { percentage += 25; parts.push("Demonstrator"); }
  if (exercise.target_muscles && exercise.target_muscles.length > 0) { percentage += 25; parts.push("Muscles"); }
  if (evidenceCount > 0) { percentage += 25; parts.push(`Evidence (${evidenceCount})`); }
  return { percentage, tooltip: parts.length ? `Complete: ${parts.join(', ')}` : 'No data' };
};

const TableSkeleton = () => (
  <div className="space-y-3 p-4">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="flex items-center gap-4">
        <Skeleton className="h-16 w-16 rounded-lg" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-3 w-32" />
        </div>
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-8 w-16" />
      </div>
    ))}
  </div>
);

const Exercises = () => {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can('exercises.edit');
  const {
    searchTerm: simpleSearchTerm,
    setSearchTerm: setSimpleSearchTerm,
    exercises: simpleExercises,
    loading: simpleLoading,
    error: simpleError,
    page,
    nextPage,
    previousPage,
    hasMore,
    hasPreviousPage,
    refetch,
  } = useSimpleExerciseSearch();

  const { isBookmarked, toggleBookmark } = useExerciseBookmarks();
  const { athletes } = useAthletes();
  const [evidenceFetching, setEvidenceFetching] = useState({});
  const [evidenceCounts, setEvidenceCounts] = useState({});
  const [assigningDemonstrator, setAssigningDemonstrator] = useState({});
  const [quickFilter, setQuickFilter] = useState('all');
  const [bookmarkForAthleteExercise, setBookmarkForAthleteExercise] = useState(null);

  useEffect(() => {
    if (simpleExercises.length === 0) return;
    const ids = simpleExercises.map(e => e.id);
    supabase
      .from('exercise_evidence')
      .select('exercise_id, review_status')
      .in('exercise_id', ids)
      .then(({ data, error }) => {
        if (error || !data) return;
        const counts = {};
        for (const row of data) {
          if (!counts[row.exercise_id]) counts[row.exercise_id] = { approved: 0, pending: 0 };
          if (row.review_status === 'pending') {
            counts[row.exercise_id].pending++;
          } else if (row.review_status === 'approved' || !row.review_status) {
            counts[row.exercise_id].approved++;
          }
        }
        setEvidenceCounts(counts);
      });
  }, [simpleExercises]);

  const assignDemonstrator = async (exerciseId, athleteId, athleteName) => {
    setAssigningDemonstrator(prev => ({ ...prev, [exerciseId]: true }));
    try {
      const { error } = await supabase.rpc('admin_assign_demonstrator', {
        p_exercise_id: exerciseId,
        p_athlete_id: athleteId,
      });
      if (error) throw error;
      toast.success(athleteId ? `Assigned ${athleteName}` : 'Demonstrator removed');
      refetch();
    } catch (err) {
      toast.error('Failed to assign demonstrator', { description: err.message });
    } finally {
      setAssigningDemonstrator(prev => ({ ...prev, [exerciseId]: false }));
    }
  };

  const filteredExercises = useMemo(() => {
    if (quickFilter === 'all') return simpleExercises;
    if (quickFilter === 'has_video') return simpleExercises.filter(e => e.has_video);
    if (quickFilter === 'no_video') return simpleExercises.filter(e => !e.has_video);
    if (quickFilter === 'bookmarked') return simpleExercises.filter(e => isBookmarked(e.id));
    return simpleExercises;
  }, [simpleExercises, quickFilter, isBookmarked]);

  const fetchEvidenceForExercise = async (exerciseId, exerciseName) => {
    setEvidenceFetching(prev => ({ ...prev, [exerciseId]: true }));
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        toast.error("You must be logged in to fetch evidence.");
        throw new Error("User not authenticated");
      }
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetchEvidence?exercise_id=${exerciseId}&min_relevance=3`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Network response was not ok');
      }
      const data = await response.json();
      if (data.success) {
        const { newConnections, totalArticlesFound, updatedRecords } = data.summary;
        const parts = [];
        if (totalArticlesFound > 0) parts.push(`${totalArticlesFound} articles found`);
        if (newConnections > 0) parts.push(`${newConnections} new`);
        if (updatedRecords > 0) parts.push(`${updatedRecords} enriched`);
        toast.success(`Evidence updated for ${exerciseName}`, {
          description: parts.join(' · ') || 'No new articles found',
        });
        if (newConnections > 0) {
          setEvidenceCounts(prev => {
            const current = prev[exerciseId] || { approved: 0, pending: 0 };
            return { ...prev, [exerciseId]: { ...current, pending: current.pending + newConnections } };
          });
        }
      } else {
        throw new Error(data.message || 'Failed to fetch evidence.');
      }
    } catch (err) {
      if (err.message !== "User not authenticated") {
        toast.error("Failed to fetch evidence", { description: err.message });
      }
    } finally {
      setEvidenceFetching(prev => ({ ...prev, [exerciseId]: false }));
    }
  };

  const handleRowClick = (exerciseId) => navigate(`/exercise/${exerciseId}`);

  const handleBookmark = (e, exerciseId) => {
    e.stopPropagation();
    toggleBookmark(exerciseId);
  };

  const handleCopyExerciseName = async (e, name) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(name);
      toast.success('Exercise name copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const quickFilters = [
    { id: 'all', label: 'All' },
    { id: 'has_video', label: 'Has Video' },
    { id: 'no_video', label: 'No Video' },
    { id: 'bookmarked', label: 'Bookmarked' },
  ];

  return (
    <>
      <Helmet><title>Exercises - My Movement Space</title></Helmet>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Exercises</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Browse, manage, and analyze the exercise library.
            </p>
          </div>
          <Link to="/exercises/search">
            <Button variant="outline" size="sm">
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              Advanced Search
            </Button>
          </Link>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by exercise name..."
                  value={simpleSearchTerm || ''}
                  onChange={(e) => setSimpleSearchTerm(e.target.value)}
                  className="pl-9"
                  disabled={simpleLoading}
                />
              </div>
            </div>
            {/* Quick filter chips */}
            <div className="flex flex-wrap gap-1.5 mt-3">
              {quickFilters.map(f => (
                <Button
                  key={f.id}
                  variant={quickFilter === f.id ? 'default' : 'outline'}
                  size="xs"
                  onClick={() => setQuickFilter(f.id)}
                >
                  {f.label}
                </Button>
              ))}
              <span className="text-xs text-muted-foreground self-center ml-2">
                {filteredExercises.length} exercise{filteredExercises.length !== 1 ? 's' : ''}
              </span>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {simpleLoading && simpleExercises.length === 0 ? (
              <TableSkeleton />
            ) : simpleError ? (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-sm text-destructive">{simpleError}</p>
              </div>
            ) : filteredExercises.length === 0 ? (
              <EmptyState
                icon={Search}
                title="No exercises found"
                description={quickFilter !== 'all' ? 'Try a different filter or clear your selection.' : 'Try different search terms.'}
                action={() => { setSimpleSearchTerm(''); setQuickFilter('all'); }}
                actionLabel="Clear filters"
              />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Demonstrator</TableHead>
                      <TableHead className="text-center">Video</TableHead>
                      <TableHead className="text-center">Narration</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredExercises.map(exercise => {
                      const evData = evidenceCounts[exercise.id] || { approved: 0, pending: 0 };
                      const evApproved = evData.approved;
                      const evPending = evData.pending;
                      const { percentage, tooltip } = calculateCompletion(exercise, evApproved);
                      const bookmarked = isBookmarked(exercise.id);
                      return (
                        <TableRow key={exercise.id} className="cursor-pointer" onClick={() => handleRowClick(exercise.id)}>
                          <TableCell className="w-14 pr-0" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-0.5">
                              <button
                                onClick={(e) => handleBookmark(e, exercise.id)}
                                className={`p-1 rounded hover:bg-muted transition-colors ${bookmarked ? 'text-amber-500' : 'text-muted-foreground/40 hover:text-muted-foreground'}`}
                              >
                                {bookmarked ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setBookmarkForAthleteExercise(exercise); }}
                                className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground/40 hover:text-muted-foreground"
                                title="Bookmark for athlete"
                              >
                                <UserPlus className="h-4 w-4" />
                              </button>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {exercise.video_thumbnail_url ? (
                                <div
                                  className="relative group overflow-hidden rounded-md shrink-0"
                                  onClick={(e) => { e.stopPropagation(); exercise.video_player_url && window.open(exercise.video_player_url, '_blank'); }}
                                >
                                  <img src={exercise.video_thumbnail_url} alt={exercise.name} className="w-14 h-14 rounded-md object-cover border" />
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                    <Play className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                </div>
                              ) : (
                                <div className="w-14 h-14 rounded-md bg-muted flex items-center justify-center shrink-0">
                                  <Dumbbell className="h-5 w-5 text-muted-foreground" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <button
                                    type="button"
                                    onClick={(e) => handleCopyExerciseName(e, exercise.name)}
                                    className="p-1 rounded shrink-0 text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted transition-colors"
                                    title="Copy exercise name"
                                    aria-label="Copy exercise name"
                                  >
                                    <Copy size={16} weight="regular" />
                                  </button>
                                  <p className="text-sm font-medium truncate min-w-0">{exercise.name}</p>
                                </div>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="mt-1.5 flex items-center gap-2 max-w-[140px]">
                                        <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                                          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${percentage}%` }} />
                                        </div>
                                        <span className="text-[10px] text-muted-foreground font-medium">{percentage}%</span>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent><p className="text-xs">{tooltip}</p></TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            {!canEdit ? (
                              <span className="flex items-center gap-1.5 text-xs">
                                <User className="h-3.5 w-3.5 text-muted-foreground" />
                                {exercise.demonstrator_name || <span className="text-muted-foreground/50 italic">Unassigned</span>}
                              </span>
                            ) : exercise.demonstrator_name ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="flex items-center gap-1.5 hover:opacity-70 transition-opacity">
                                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                                    <span className="text-xs">{exercise.demonstrator_name}</span>
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-48">
                                  <DropdownMenuLabel className="text-xs">Reassign Demonstrator</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  {athletes.map(athlete => (
                                    <DropdownMenuItem
                                      key={athlete.id}
                                      onClick={() => assignDemonstrator(exercise.id, athlete.id, athlete.name)}
                                      className="text-xs gap-2"
                                    >
                                      {exercise.demonstrator_id === athlete.id && (
                                        <Check className="h-3 w-3 text-emerald-500" />
                                      )}
                                      <span className={exercise.demonstrator_id !== athlete.id ? 'pl-5' : ''}>{athlete.name}</span>
                                    </DropdownMenuItem>
                                  ))}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => assignDemonstrator(exercise.id, null, null)}
                                    className="text-xs text-destructive gap-2"
                                  >
                                    <X className="h-3 w-3" />
                                    <span>Remove Demonstrator</span>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="xs"
                                    disabled={assigningDemonstrator[exercise.id]}
                                    className="gap-1 text-muted-foreground/50 italic"
                                  >
                                    {assigningDemonstrator[exercise.id] ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <UserPlus className="h-3 w-3" />
                                    )}
                                    Unassigned
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-48">
                                  <DropdownMenuLabel className="text-xs">Assign Demonstrator</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  {athletes.map(athlete => (
                                    <DropdownMenuItem
                                      key={athlete.id}
                                      onClick={() => assignDemonstrator(exercise.id, athlete.id, athlete.name)}
                                      className="text-xs gap-2"
                                    >
                                      <span>{athlete.name}</span>
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="inline-flex items-center justify-center">
                                    {exercise.has_video ? <Video className="h-4 w-4 text-emerald-500" /> : <VideoOff className="h-4 w-4 text-muted-foreground/30" />}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent><p className="text-xs">{exercise.has_video ? 'Video available' : 'No video'}</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                          <TableCell className="text-center">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="inline-flex items-center justify-center">
                                    {exercise.has_narration ? <Mic className="h-4 w-4 text-emerald-500" /> : <MicOff className="h-4 w-4 text-muted-foreground/30" />}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent><p className="text-xs">{exercise.has_narration ? 'Voice-over available' : 'No voice-over'}</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-2">
                              {(evApproved > 0 || evPending > 0) && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center gap-1">
                                        <Microscope className={`h-3.5 w-3.5 ${evPending > 0 ? 'text-amber-500' : 'text-blue-500'}`} />
                                        {evApproved > 0 && <span className="text-xs font-medium text-blue-500">{evApproved}</span>}
                                        {evPending > 0 && <span className="text-xs font-medium text-amber-500">+{evPending}</span>}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs">
                                        {evApproved > 0 && `${evApproved} approved`}
                                        {evApproved > 0 && evPending > 0 && ', '}
                                        {evPending > 0 && `${evPending} pending review`}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {canEdit && (
                              <Button
                                variant={(evApproved > 0 || evPending > 0) ? "ghost" : "outline"}
                                size="xs"
                                onClick={() => fetchEvidenceForExercise(exercise.id, exercise.name)}
                                disabled={evidenceFetching[exercise.id]}
                                className="gap-1"
                              >
                                {evidenceFetching[exercise.id] ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <BookOpen className="h-3 w-3" />
                                )}
                                {(evApproved > 0 || evPending > 0) ? 'Refresh' : 'Fetch'}
                              </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                <div className="flex items-center justify-between border-t px-4 py-3">
                  <span className="text-xs text-muted-foreground">Page {page + 1}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="xs" onClick={previousPage} disabled={!hasPreviousPage || simpleLoading}>Previous</Button>
                    <Button variant="outline" size="xs" onClick={nextPage} disabled={!hasMore || simpleLoading}>Next</Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <BookmarkForAthleteDialog
        exerciseId={bookmarkForAthleteExercise?.id}
        exerciseName={bookmarkForAthleteExercise?.name}
        open={!!bookmarkForAthleteExercise}
        onOpenChange={(open) => { if (!open) setBookmarkForAthleteExercise(null); }}
      />
    </>
  );
};

export default Exercises;
